import * as turf from '@turf/turf';
import { uniq } from 'lodash';
import { MapManager } from '@/core/modules/MapManager';
import { ASSETS_LAYER as LAYER, ASSETS_SOURCE as SOURCE, TextColor } from './AssetsMapDrawer.constants';
import { ImageName, applyStaticEventCoords, createPopupContentElement, loadImageUrl } from '@/components/composite/GeoMap/mapImages';
import type { AssetsGeoJsonData, RenderAssetsItem } from './AssetsMapDrawer.types';

const DEFAULT_ASSET_IMPACT_RADIUS_KM = 2;

/**
 * Assets + selected-asset proximity overlay as a composable map building block.
 * Owns the `assets-*` sources/layers and their listeners; exposes the same `clear()`/`setVisible()`
 * contract as `MapDataDrawer`.
 */
export class AssetsMapDrawer {
  map: MapManager | undefined;
  private listeners: { dispose(): void }[] = [];
  protected visible = true;

  protected getOwnedLayerIds(): string[] {
    return [LAYER.ASSETS, LAYER.ASSETS_CIRCLE_PROXIMITY];
  }

  protected getOwnedSourceIds(): string[] {
    return [SOURCE.ASSETS, SOURCE.ASSETS_CIRCLE_PROXIMITY];
  }

  private applyVisibility(layerId: string): void {
    this.map?.setLayoutProperty(layerId, 'visibility', this.visible ? 'visible' : 'none');
  }

  setMap = (map: MapManager) => {
    this.map = map;
    return this;
  };

  hasMap = () => !!this.map;

  resetActiveTargets = () => {
    this.map?.setLayoutProperty(LAYER.ASSETS, 'icon-image', [
      'match',
      ['get', 'icon'],
      ImageName.ASSET_ACTIVE_ICON,
      ImageName.ASSET_ICON,
      ['get', 'icon'],
    ]);
  };

  renderAssets = (args: {
    items: RenderAssetsItem[];
    onClick: (item: RenderAssetsItem, config: { metaKey: boolean }) => void;
  }) => {
    const source = this.map?.getSource(SOURCE.ASSETS);

    if (this.hasMap()) {
      const customIcons = args.items.filter((item) => item.icon).map<string>((item) => item.icon!);
      uniq(customIcons).forEach((iconUrl) => loadImageUrl({ map: this.map!.getMap()!, imageUrl: iconUrl }));
    }

    const data: AssetsGeoJsonData = {
      type: 'FeatureCollection',
      features: args.items.map((item) => ({
        type: 'Feature',
        properties: {
          ...item,
          icon: item.icon || ImageName.ASSET_ICON, // fallback to default
        },
        geometry: {
          type: 'Point',
          coordinates: [item.longitude, item.latitude],
        },
      })),
    };

    if (source) {
      this.map?.updateSourceData(SOURCE.ASSETS, data);
    } else {
      this.map?.addSource(SOURCE.ASSETS, { type: 'geojson', data });
      this.map?.addLayer({
        id: LAYER.ASSETS,
        source: SOURCE.ASSETS,
        type: 'symbol',
        layout: {
          'icon-image': ['get', 'icon'],
          'icon-size': ['interpolate', ['linear'], ['zoom'], 10, 0.5, 15, 1],
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          // Ensure icons remain visible even when text labels can't be placed
          'text-optional': true,
          'text-field': ['get', 'title'],
          'text-size': 14,
          'text-offset': [0, 2],
          'text-font': ['DIN Pro Regular', 'Arial Unicode MS Regular'],
        },
        paint: {
          'text-color': TextColor.Neutral,
        },
      });
      this.applyVisibility(LAYER.ASSETS);

      // Update icon for active selected asset
      const setActiveAsset = ({ id, icon }: Pick<RenderAssetsItem, 'id' | 'icon'>) => {
        // Don't apply selected icon for custom icons
        if (icon !== ImageName.ASSET_ICON) {
          return;
        }
        this.map
          ?.getMap()
          ?.setLayoutProperty(LAYER.ASSETS, 'icon-image', [
            'match',
            ['get', 'id'],
            id,
            ImageName.ASSET_ACTIVE_ICON,
            ['get', 'icon'],
          ]);
      };

      const disposeOnClick = this.map?.addLayerMouseEvents(LAYER.ASSETS).on('click', (e) => {
        const properties = e.features![0].properties as RenderAssetsItem;
        const currentPoint = properties.id;

        // Means "command" key on MacOS, or "Alt" key on Win
        const metaKey = e.originalEvent.metaKey || e.originalEvent.altKey;
        args.onClick(properties, { metaKey });

        if (!metaKey) {
          setActiveAsset({ id: currentPoint, icon: properties.icon });
        }
      });

      const disposeOnMouseover = this.map?.addLayerMouseEvents(LAYER.ASSETS).on('mouseover', (e) => {
        const properties = e.features![0].properties as RenderAssetsItem;
        applyStaticEventCoords(e, properties); // Make always popup on target coords (not mouse event)
        this.map?.showPopup(e, {
          offset: [0, -12],
          layerId: LAYER.ASSETS,
          content: createPopupContentElement({ title: properties.title }),
        });
      });

      this.listeners.push(...[disposeOnClick, disposeOnMouseover].filter((l) => !!l));
    }
  };

  renderSelectedAssetProximity = (assets: RenderAssetsItem[]) => {
    const circles = assets.map((asset) => {
      const radius = asset.impactRadius || DEFAULT_ASSET_IMPACT_RADIUS_KM;
      return turf.circle([asset.longitude, asset.latitude], radius, { units: 'kilometers' });
    });

    const data: Parameters<MapManager['addSource']>[1]['data'] = {
      type: 'FeatureCollection',
      features: circles,
    };

    if (this.map?.getSource(SOURCE.ASSETS_CIRCLE_PROXIMITY)) {
      this.map.updateSourceData(SOURCE.ASSETS_CIRCLE_PROXIMITY, data);
    } else {
      this.map?.addSource(SOURCE.ASSETS_CIRCLE_PROXIMITY, {
        type: 'geojson',
        data,
      });

      this.map?.addLayer({
        id: LAYER.ASSETS_CIRCLE_PROXIMITY,
        source: SOURCE.ASSETS_CIRCLE_PROXIMITY,
        type: 'fill',
        paint: {
          'fill-color': '#F56F4A', // var(--m-Sienna), local variables does not work with mapbox
          'fill-opacity': 0.2,
        },
      });
      this.applyVisibility(LAYER.ASSETS_CIRCLE_PROXIMITY);
    }
  };

  clear = (): void => {
    this.listeners.forEach((l) => l.dispose());
    this.listeners = [];

    this.getOwnedLayerIds().forEach((id) => this.map?.removeLayer(id));
    this.getOwnedSourceIds().forEach((id) => this.map?.removeSource(id));
  };

  setVisible = (visible: boolean): void => {
    this.visible = visible;
    this.getOwnedLayerIds().forEach((id) => this.applyVisibility(id));
  };
}
